/*
XOL-645: the document a captured request e-mail is shown in (MailPreviewDialog's
<iframe srcdoc>). The stored HTML is NEVER put in the SPA's own DOM: it goes in a sandboxed
frame (no scripts, no same-origin), and this function is the second layer around it.

What it does, in order:
  1. Parses the e-mail INERTLY (DOMParser: no browsing context, so nothing runs or loads).
  2. Removes <script>, <base>, <meta http-equiv> (refresh, CSP, set-cookie), any mail-supplied
     <meta name="referrer"> (the last one wins, so ours must be the only one), nested frames
     (iframe, frame, object, embed: a nested srcdoc is never scanned) and declarative shadow
     roots (<template shadowrootmode>: inert for DOMParser, live for the frame's own parser).
  3. Keeps an href on ANY element (<a>, <area>, SVG <a xlink:href>, ...) only when its scheme is
     http:, https: or mailto: (javascript: and data: links are neutralised; the popup sandbox
     tokens make links live).
  4. On every <a> / <area>: drops target, rel and referrerpolicy, then sets
     rel="noopener noreferrer". allow-popups-to-escape-sandbox gives the new tab a normal
     context, so a mail's rel="opener" or named target would otherwise hand it a live opener
     (reverse tabnabbing).
  5. Puts, FIRST in <head>: a Content-Security-Policy meta that blocks every network load
     (remote images, CSS, fonts, pixels), a no-referrer meta, and <base target="_blank"> so a
     link opens a new tab and never navigates the frame.
  6. Returns '<!DOCTYPE html>' + the document, so the frame is not in quirks mode.

It deliberately does NOT run sanitiseHtml.js (DOMPurify): that strips <style> and <meta> and
would change what the e-mail looks like. The sandbox and the CSP are the boundary; this is
defence in depth. ES5, no optional chaining or nullish coalescing (Babel 6).
*/

var CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:"

var SAFE_HREF = /^(https?:|mailto:)/

function removeAll (doc, selector) {
  var found = doc.querySelectorAll(selector)

  for (var i = 0; i < found.length; i++) {
    if (found[i].parentNode) {
      found[i].parentNode.removeChild(found[i])
    }
  }
}

// A browser ignores tabs, newlines and leading control characters inside a URL
// ("java\tscript:"), so the test ignores them too.
function isSafeHref (href) {
  var text = String(href)
  var kept = ''

  for (var i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) > 32) {
      kept += text.charAt(i)
    }
  }

  return SAFE_HREF.test(kept.toLowerCase())
}

// Any attribute whose local name is href, in ANY namespace (xlink:href on an SVG <a>), on ANY element.
function guardHrefs (doc) {
  var all = doc.querySelectorAll('*')

  for (var i = 0; i < all.length; i++) {
    var attrs = Array.prototype.slice.call(all[i].attributes)

    for (var k = 0; k < attrs.length; k++) {
      if (attrs[k].localName === 'href' && !isSafeHref(attrs[k].value)) {
        all[i].removeAttributeNode(attrs[k])
      }
    }
  }
}

// A link opens a new tab with NO way back to the SPA: nothing from the mail survives that could
// give the new tab an opener (rel="opener", a named target) or a referrer.
function hardenLinks (doc) {
  var links = doc.querySelectorAll('a, area')

  for (var i = 0; i < links.length; i++) {
    links[i].removeAttribute('target')
    links[i].removeAttribute('rel')
    links[i].removeAttribute('referrerpolicy')
    links[i].setAttribute('rel', 'noopener noreferrer')
  }
}

// The mail's own <meta name="referrer"> would beat ours (the last one wins).
function removeReferrerMetas (doc) {
  var metas = doc.querySelectorAll('meta[name]')

  for (var i = 0; i < metas.length; i++) {
    if (String(metas[i].getAttribute('name')).trim().toLowerCase() === 'referrer' && metas[i].parentNode) {
      metas[i].parentNode.removeChild(metas[i])
    }
  }
}

export function previewSrcdoc (html) {
  if (typeof html !== 'string' || html === '' || typeof DOMParser === 'undefined') {
    return ''
  }

  var doc = new DOMParser().parseFromString(html, 'text/html')

  removeAll(doc, 'script, base, meta[http-equiv], iframe, frame, object, embed, template[shadowrootmode], template[shadowroot]')
  removeReferrerMetas(doc)
  guardHrefs(doc)
  hardenLinks(doc)

  var csp = doc.createElement('meta')
  csp.setAttribute('http-equiv', 'Content-Security-Policy')
  csp.setAttribute('content', CSP)

  var referrer = doc.createElement('meta')
  referrer.setAttribute('name', 'referrer')
  referrer.setAttribute('content', 'no-referrer')

  var base = doc.createElement('base')
  base.setAttribute('target', '_blank')

  // insertBefore the current first child, last-to-first, so the order ends up CSP, referrer, base
  var head = doc.head
  var first = head.firstChild

  head.insertBefore(base, first)
  head.insertBefore(referrer, base)
  head.insertBefore(csp, referrer)

  return '<!DOCTYPE html>' + doc.documentElement.outerHTML
}

export default previewSrcdoc
